import React, { useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import '../styles/components.css';
import './DocumentGalleryView.css';
import LanguageContext from '../helper/LanguageContext';
import { SET_VIEWER_IINITAL_INDEX, SET_VIEWER_IMAGES, SET_VIEWER_IS_INTERNAL } from '../store';
import {
  buildDocViewerItems,
  canPreviewDoc,
  getDocAccentColor,
  getDocExtension,
  getDocIconName,
  getDocKind,
  getDocMessageName,
} from '../utils/documentAlbum';

const VISIBLE_TILES = 4;

/**
 * Album bubble for a batch of documents, the document counterpart of
 * `ImagesWithVideos`. PDFs and images open the shared full-screen viewer.
 * Office formats that the Media DocViewer can preview are opened there instead.
 */
function DocumentGalleryView({ documents, isInternal = false, caption = null, onPreviewDoc }) {
  const dispatch = useDispatch();
  const { strings } = useContext(LanguageContext);

  const docs = useMemo(() => (Array.isArray(documents) ? documents : []), [documents]);
  const visible = docs.slice(0, VISIBLE_TILES);
  const overflow = docs.length - visible.length;

  if (docs.length === 0) return null;

  const openViewer = (index) => {
    const doc = docs[index];
    const kind = getDocKind(doc);
    if (
      typeof onPreviewDoc === 'function' &&
      canPreviewDoc(doc) &&
      kind !== 'pdf' &&
      kind !== 'image' &&
      kind !== 'spreadsheet'
    ) {
      onPreviewDoc(doc);
      return;
    }
    dispatch({ type: SET_VIEWER_IINITAL_INDEX, index });
    dispatch({ type: SET_VIEWER_IMAGES, ViewerImages: buildDocViewerItems(docs) });
    dispatch({ type: SET_VIEWER_IS_INTERNAL, isInternal });
  };

  return (
    <div className="doc-gallery">
      {/* Every element here is a div on purpose: the chat bubble forces its own
          font size and colour onto any span it contains. */}
      <div className="doc-gallery-header">
        <div className="material-symbols-outlined doc-gallery-header-icon">
          folder_copy
        </div>
        <div className="doc-gallery-header-text">
          {docs.length} {strings.documents || 'Documents'}
        </div>
      </div>

      <div className="doc-gallery-grid">
        {visible.map((doc, index) => {
          const name = getDocMessageName(doc);
          const extension = getDocExtension(doc);
          const color = getDocAccentColor(doc);
          const isLast = index === visible.length - 1 && overflow > 0;

          return (
            <div
              key={doc?.id || doc?.messageRequestId || `${name}-${index}`}
              className="doc-gallery-tile document-gallery-view__tile"
              title={name}
              role="button"
              tabIndex={0}
              style={{ '--dg-accent': color, '--dg-tint': `${color}0f`, '--dg-edge': `${color}26` }}
              onClick={(e) => {
                e.stopPropagation();
                openViewer(index);
              }}
            >
              <div className="doc-gallery-tile-top">
                <div
                  className="material-symbols-outlined doc-gallery-tile-icon document-gallery-view__icon"
                >
                  {getDocIconName(doc)}
                </div>
                {extension ? (
                  <div className="doc-gallery-tile-badge document-gallery-view__badge">
                    {extension.toUpperCase()}
                  </div>
                ) : null}
              </div>
              <div className="doc-gallery-tile-name">{name}</div>
              {isLast ? (
                <div className="doc-gallery-tile-overflow">+{overflow}</div>
              ) : null}
            </div>
          );
        })}
      </div>

      {caption ? <div className="doc-gallery-caption">{caption}</div> : null}
    </div>
  );
}

export default DocumentGalleryView;
